import {onSnapshot, where} from "firebase/firestore";
import Event from "@/models/Event";
import constants from "@/shared/constants";
import {
    getQueryActivitiesCollection,
    getQueryConnectionsCollection,
    getQueryEventAttendeesCollection, getQueryEventCollection,
    getQueryEventNotesCollection,
    getQueryFeaturedCompaniesCollection,
    getQuerySponsorCollection,
    queryEventCollection
} from "@/shared/firebase_utils";

const state = {
    multiConnectEvents: [],
    allMultiConnectEvents: [],
    linkedSubEvents: [],
    linkedSessions: [],
    eventConnections: null,
    eventAttendees: null,
    eventPartners: null,
    eventActivities: null,
    eventNotes: null,
    eventMeetings: null,
};

const getters = {
    multiConnectEvents: (state) => state.multiConnectEvents,
    totalNoOfConnections: (state, getters, rootState, rootGetters) => {
        let noOfConnections = 0;

        state.multiConnectEvents.forEach(multiConnectEvent => {
            if (multiConnectEvent.noOfConnections != undefined)
                noOfConnections += multiConnectEvent.noOfConnections;
        });


        return noOfConnections;
    },
    hasUpcomingEvents: (state) => {
        return state.multiConnectEvents.some(multiConnectEvent => {
            return multiConnectEvent.getStatus() == 'Upcoming'

        });
    },
    allMultiConnectEvents: (state) => state.allMultiConnectEvents,
    noOfActiveEvents: (state, getters, rootState, rootGetters) => {
        let activeEvents;
        if (rootGetters.isSuperUser) {
            activeEvents = state.allMultiConnectEvents.filter((event) => {
                return (event.getStatus() == 'Active' && event.businessProfileId == rootGetters.userProfile.businessProfileId);
            });

        } else {
            activeEvents = state.multiConnectEvents.filter(multiConnectEvent => {
                return multiConnectEvent.getStatus() == 'Active'

            });
        }

        return activeEvents?.length || 0;
    },
    upcomingEvents: (state, getters, rootState, rootGetters) => {
        let upcomingEvents;
        if (rootGetters.isSuperUser) {
            upcomingEvents = state.allMultiConnectEvents.filter((event) => {
                return (event.getStatus() == 'Upcoming' && event.businessProfileId == rootGetters.userProfile.businessProfileId);
            });

        } else {
            upcomingEvents = state.multiConnectEvents.filter(multiConnectEvent => {
                return multiConnectEvent.getStatus() == 'Upcoming'

            });
        }

        return upcomingEvents;
    },
    noOfUpcomingEvents: (state, getters) => getters.upcomingEvents?.length || 0,
    totalNoOfRegistrants: (state, getters) => {
        let noOfRegistrants = 0;
        getters.upcomingEvents.forEach((event) => {
            if (event.attendees && event.attendees.length > 0)
                noOfRegistrants += event.attendees.length;
        });
        return noOfRegistrants;
    },
    linkedSubEvents: (state) => state.linkedSubEvents,
    linkedSessions: (state) => state.linkedSessions,
    eventConnections: (state) => state.eventConnections,
    eventAttendees: (state) => state.eventAttendees,
    eventPartners: (state) => state.eventPartners,
    eventStartupsnScaleups: (state) => state.eventPartners.filter(partner => partner.promoterType == "startup" || partner.promoterType == "scaleup"),
    eventActivities: (state) => state.eventActivities,
    eventNotes: (state) => state.eventNotes,
    eventMeetings: (state) => state.eventMeetings,
};

const actions = {
    async onMultiConnectEventsSnapshot({commit, rootGetters}, changes) {

        //Store all MC events from a collection in the state
        if (rootGetters.isSuperUser)
            commit("setAllMultiConnectEvents", changes);
        else {
            //Store all MC events for a business profile in the state
            commit("setMultiConnectEvents", changes);
        }

    },
    clearMultiConnectEvents: ({commit, rootGetters}) => {
        if (rootGetters.isSuperUser)
            commit("clearAllMultiConnectEvents")
        else
            commit("clearMultiConnectEvents");

    },
    initializeLinkedSubEvents({commit}, payload) {
        try {
            let unsubscribeSubEventsSnapshot = onSnapshot(queryEventCollection(
                    where("businessProfileId", "==", payload.businessProfileId),
                    where("parentMultiConnectEventId", "==", payload.parentEventId),
                    where("eventCategory", "==", constants.EVENT_CATEGORY.subEvent),
                    where("parentSubEventId", "==", "")),
                (querySnapshot) => {
                    const changes = querySnapshot.docChanges();

                    commit("setLinkedSubEvents", changes);
                });
        } catch (error) {
            console.log(
                "Error occurred while fetching snapshot of events for super user: ",
                error
            );
        }
    },
    clearLinkedSubEvents({commit}) {
        commit("clearLinkedSubEvents");
    },
    initializeLinkedSessions({commit}, payload) {
        try {
            //Retrieve docs with above ids from firestore
            let unsubscribeSessionsSnapshot = onSnapshot(queryEventCollection(
                    where("businessProfileId", "==", payload.businessProfileId),
                    where("parentMultiConnectEventId", "==", payload.parentEventId),
                    where("eventCategory", "==", constants.EVENT_CATEGORY.session)),
                (querySnapshot) => {
                    const changes = querySnapshot.docChanges();
                    console.log("changes: ", changes);
                    commit("setLinkedSessions", changes);
                });
        } catch (error) {
            console.log(
                "Error occurred while fetching snapshot of sessions: ",
                error
            );
        }
    },
    clearLinkedSessions({commit}) {
        commit("clearLinkedSessions");
    },
    async fetchEventConnections({commit}, eventId) {
        try {
            const eventConnections = await getQueryConnectionsCollection(where("multiConnectEventId", "==", eventId));
            commit("setEventConnections", eventConnections.docs.map((doc) => doc.data()));
        } catch (error) {
            console.log("Error occurred while fetching event connections: ", error);
        }
    },
    clearEventConnections({commit}) {
        commit("clearEventConnections");
    },
    async fetchEventAttendees({commit}, event) {
        try {

            const eventAttendees = await getQueryEventAttendeesCollection(where("eventId", "==", event.id));
            const attendees = eventAttendees.docs.map((doc) => doc.data());
            event.attendees.forEach((attendee) => {
                const index = attendees.findIndex((e) => e.email === attendee.email);
                if (index == -1) {
                    attendees.push(attendee);
                } else {
                    attendees[index] = attendee;
                }
            });
            commit("setEventAttendees", attendees);
        } catch (error) {
            console.log("Error occurred while fetching event attendees: ", error);
        }
    },
    clearEventAttendees({commit}) {
        commit("clearEventAttendees");
    },
    async initializePartners({commit}, eventId) {
        const allPartners = [];

        const partners = getQuerySponsorCollection(where('eventId', "==", eventId));
        const featuredCompanies = getQueryFeaturedCompaniesCollection(where('eventId', "==", eventId));
        await Promise.all([partners, featuredCompanies]).then((values) => {
            const partners = values[0];
            const featuredCompanies = values[1];
            partners.forEach((partner) => {
                allPartners.push(partner.data());
            });
            featuredCompanies.forEach((company) => {
                allPartners.push(company.data());
            });
        });
        commit("setEventPartners", allPartners);
    },
    clearEventPartners({commit}) {
        commit("clearEventPartners");
    },
    async initializeActivities({commit}, eventId) {
        const activities = await getQueryActivitiesCollection(where('eventId', "==", eventId));
        commit("setEventActivities", activities.docs.map((doc) => doc.data()));
    },
    clearEventActivities({commit}) {
        commit("clearEventActivities");
    },
    async initializeNotes({commit}, eventId) {
        const notes = await getQueryEventNotesCollection(where('eventId', "==", eventId));
        commit("setEventNotes", notes.docs.map((doc) => doc.data()));
    },
    clearEventNotes({commit}) {
        commit("clearEventNotes");
    },
    async initializeMeetings({commit}, eventId) {

        const meetings = await getQueryEventCollection(
            where('parentMultiConnectEventId', "==", eventId),
            where("eventCategory", "==", "superConnect")
        );
        commit("setEventMeetings", meetings.docs.map((doc) => doc.data()));
    },
    clearEventMeetings({commit}) {
        commit("clearEventMeetings");
    },
};

const mutations = {
    setMultiConnectEvents: (state, changes) => {

        changes.forEach((change) => {

            let addedEvent;
            let isMultiConnectEvent = false;
            if (change.type === "added" || change.type == "modified") {
                //let data = change.doc.data({ serverTimestamps: "estimate" });
                addedEvent = Event.fromFireStore(change.doc);
                if (addedEvent.eventCategory == undefined || addedEvent.eventCategory == "" || addedEvent.eventCategory == "multiConnect" || addedEvent.eventCategory == "travel")
                    isMultiConnectEvent = true;
            }

            if (isMultiConnectEvent && change.type === "added") {
                if (state.multiConnectEvents.length != 0 && addedEvent.createdAt > state.multiConnectEvents[0].createdAt) {
                    state.multiConnectEvents.unshift(addedEvent);
                } else {
                    state.multiConnectEvents.push(addedEvent);

                }
            } else if (isMultiConnectEvent && change.type == "modified") {
                //Replace the old object within the array with the modified object
                state.multiConnectEvents.forEach((item, index) => {
                    if (item.id == change.doc.id) {
                        state.multiConnectEvents.splice(index, 1, addedEvent);
                    }
                });
            } else if (change.type == "removed") {
                //Remove the object from the array
                state.multiConnectEvents.forEach((item, index) => {
                    if (item.id == change.doc.id) {
                        state.multiConnectEvents.splice(index, 1);
                    }
                });
            }

        });
    },
    clearMultiConnectEvents: (state) => {
        state.multiConnectEvents = [];
    },
    setAllMultiConnectEvents: (state, changes) => {
        changes.forEach((change) => {
            let isMultiConnectEvent = false;
            let addedEvent;
            if (change.type === "added" || change.type == "modified") {
                addedEvent = Event.fromFireStore(change.doc);
                if (addedEvent.eventCategory == undefined || addedEvent.eventCategory == "" || addedEvent.eventCategory == "multiConnect" || addedEvent.eventCategory == "travel")
                    isMultiConnectEvent = true;
            }
            if (isMultiConnectEvent && change.type === "added") {
                if (state.allMultiConnectEvents.length != 0 && addedEvent.createdAt > state.allMultiConnectEvents[0].createdAt) {
                    state.allMultiConnectEvents.unshift(addedEvent);
                } else {
                    state.allMultiConnectEvents.push(addedEvent);

                }
            } else if (isMultiConnectEvent && change.type == "modified") {
                //Replace the old object within the array with the modified object
                state.allMultiConnectEvents.forEach((item, index) => {
                    if (item.id == change.doc.id) {
                        state.allMultiConnectEvents.splice(index, 1, addedEvent);
                    }
                });
            } else if (isMultiConnectEvent && change.type == "removed") {
                //Remove the object from the array
                state.allMultiConnectEvents.forEach((item, index) => {
                    if (item.id == change.doc.id) {
                        state.allMultiConnectEvents.splice(index, 1);
                    }
                });
            }
        });
    },
    clearAllMultiConnectEvents: (state) => {
        state.allMultiConnectEvents = [];
    },
    setLinkedSubEvents: (state, changes) => {
        // state.linkedSubEvents = [];
        changes.forEach((change) => {
            let addedSubEvent;
            if (change.type === "added" || change.type == "modified") {
                //let data = change.doc.data({ serverTimestamps: "estimate" });
                addedSubEvent = Event.fromFireStore(change.doc);
            }
            if (change.type === "added") {
                if (
                    state.linkedSubEvents.length != 0 &&
                    addedSubEvent.createdAt > state.linkedSubEvents[0].createdAt
                ) {
                    state.linkedSubEvents.unshift(addedSubEvent);
                } else {
                    state.linkedSubEvents.push(addedSubEvent);
                }
            } else if (change.type == "modified") {
                console.log("modified");
                //Replace the old object within the array with the modified object
                state.linkedSubEvents.forEach((item, index) => {
                    if (item.id == change.doc.id) {
                        state.linkedSubEvents.splice(index, 1, addedSubEvent);
                    }
                });
            } else if (change.type == "removed") {
                //Remove the object from the array
                state.linkedSubEvents.forEach((item, index) => {
                    if (item.id == change.doc.id) {
                        state.linkedSubEvents.splice(index, 1);
                    }
                });
            }
        });
    },
    clearLinkedSubEvents: (state) => {
        state.linkedSubEvents = [];
    },
    setLinkedSessions: (state, changes) => {
        changes.forEach((change) => {
            let addedSession;
            if (change.type === "added" || change.type == "modified") {
                //let data = change.doc.data({ serverTimestamps: "estimate" });
                addedSession = Event.fromFireStore(change.doc);
            }
            if (change.type === "added") {
                if (
                    state.linkedSessions.length != 0 &&
                    addedSession.createdAt > state.linkedSessions[0].createdAt
                ) {
                    state.linkedSessions.unshift(addedSession);
                } else {
                    state.linkedSessions.push(addedSession);
                }
            } else if (change.type == "modified") {
                //Replace the old object within the array with the modified object
                state.linkedSessions.forEach((item, index) => {
                    if (item.id == change.doc.id) {
                        state.linkedSessions.splice(index, 1, addedSession);
                    }
                });
            } else if (change.type == "removed") {
                //Remove the object from the array
                state.linkedSessions.forEach((item, index) => {
                    if (item.id == change.doc.id) {
                        state.linkedSessions.splice(index, 1);
                    }
                });
            }
        });
    },
    clearLinkedSessions: (state) => {
        state.linkedSessions = [];
    },
    setEventConnections: (state, connections) => {
        state.eventConnections = connections;
    },
    clearEventConnections: (state) => {
        state.eventConnections = null;
    },
    setEventAttendees: (state, attendees) => {
        state.eventAttendees = attendees;
    },
    clearEventAttendees: (state) => {
        state.eventAttendees = null;
    },
    setEventPartners: (state, partners) => {
        state.eventPartners = partners;
    },
    clearEventPartners: (state) => {
        state.eventPartners = null;
    },
    setEventActivities: (state, activities) => {
        state.eventActivities = activities;
    },
    clearEventActivities: (state) => {
        state.eventActivities = null;
    },
    setEventNotes: (state, notes) => {
        state.eventNotes = notes;
    },
    clearEventNotes: (state) => {
        state.eventNotes = null;
    },
    setEventMeetings: (state, meetings) => {
        state.eventMeetings = meetings;
    },
    clearEventMeetings: (state) => {
        state.eventMeetings = null;
    },
};

export default {
    state,
    getters,
    actions,
    mutations,
};